import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Checkbox } from "./components/ui/checkbox";
import { Separator } from "./components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { useToast } from "./hooks/use-toast";
import { 
  Dna, Award, Shield, Star, ArrowRight, Check, 
  Crown, Sparkles, Target, TrendingUp, Calendar,
  User, Mail, Phone, MessageSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DNA Revival Women - Luxury Landing Page
const DNARevivalLandingPage = ({ onGetStarted, onApply }) => {
  
  const handleAfterPayPayment = (packageType) => {
    const afterpayLinks = {
      blueprint: "https://afterpay.com/checkout/blueprint-697", // Placeholder - replace with actual Afterpay link
      elite: "https://afterpay.com/checkout/elite-1890", // Placeholder - replace with actual Afterpay link  
      executive: "https://afterpay.com/checkout/executive-3997" // Placeholder - replace with actual Afterpay link
    };
    
    if (packageType && afterpayLinks[packageType]) {
      window.open(afterpayLinks[packageType], '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Header with Logo */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/ebvs4qol_ChatGPT%20Image%20Aug%2023%2C%202025%20at%2009_01_36%20PM.png"
            alt="Go Figure Coaching Logo"
            className="h-16 w-auto"
          />
          <div className="flex items-center gap-4">
            <div className="text-rose-300 font-light tracking-wider text-sm uppercase">
              DNA Revival Women
            </div>
            <a 
              href={`${window.location.origin}/api/admin-safari`}
              className="text-gray-400 hover:text-rose-300 text-xs underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Admin
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center">
        {/* Background with Lisa's Photo */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/t6jlluob_IMG_0872.PNG"
            alt="Lisa Saygun"
            className="w-full h-full object-cover filter grayscale opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full mb-8">
              <Dna className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-light mb-6 tracking-wide">
            Elite, DNA-Driven
          </h1>
          <h2 className="text-6xl md:text-8xl font-light mb-8 tracking-wide">
            Coaching for <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-rose-600">Women</span>
          </h2>
          
          <div className="mb-12">
            <p className="text-xl md:text-2xl font-light tracking-wider text-gray-300 mb-2">
              Science. Strength. Longevity. Empowerment.
            </p>
          </div>
          
          <Button 
            onClick={onGetStarted}
            className="px-12 py-4 text-lg font-light tracking-widest bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white border-0 rounded-none uppercase transition-all duration-500 hover:tracking-wide"
          >
            Begin Your Revival
          </Button>
        </div>
      </div>

      {/* Client Pain Points & Empathy Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-light mb-8 tracking-wide">
              If You're Tired of Feeling Frustrated with Your Body...
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              You're not broken. You don't lack willpower. Your body just needs the right approach - one designed for YOUR unique genetics.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-rose-900/20 to-transparent border border-rose-500/30 rounded-lg p-6">
                  <h3 className="text-2xl font-medium text-rose-300 mb-4">Do You Struggle With...</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-rose-400 mr-3 mt-1">●</span>
                      <span><strong>Weight that won't budge</strong> despite "doing everything right"</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rose-400 mr-3 mt-1">●</span>
                      <span><strong>Hormonal chaos</strong> - mood swings, irregular cycles, night sweats</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rose-400 mr-3 mt-1">●</span>
                      <span><strong>Energy crashes</strong> and brain fog that derail your day</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rose-400 mr-3 mt-1">●</span>
                      <span><strong>Bloating and gut issues</strong> that make you feel uncomfortable</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rose-400 mr-3 mt-1">●</span>
                      <span><strong>Sleep problems</strong> leaving you exhausted but wired</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rose-400 mr-3 mt-1">●</span>
                      <span><strong>Workouts that feel pointless</strong> because you're not seeing results</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-gray-900/50 to-transparent border border-gray-600 rounded-lg p-6">
                  <h3 className="text-xl font-medium text-white mb-3">The Real Problem?</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Every diet, workout, and wellness program you've tried was designed for the "average" woman. But your body isn't average - it's uniquely yours. Your DNA holds the specific instructions for how YOUR body processes food, burns fat, builds muscle, and maintains hormonal balance.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-lg overflow-hidden border-4 border-rose-500/30">
                <img 
                  src="https://images.unsplash.com/photo-1590650046871-92c887180603?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwd29tZW58ZW58MHx8fHwxNzU3OTI2ODI1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Professional women collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-8 -right-8 w-16 h-16 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full opacity-80"></div>
              <div className="absolute -bottom-8 -left-8 w-12 h-12 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full opacity-60"></div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-rose-900/30 to-rose-800/30 border border-rose-500/50 rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-medium text-rose-300 mb-4">Your DNA Holds the Missing Pieces</h3>
              <p className="text-xl text-gray-200 leading-relaxed">
                When you understand your genetic blueprint, everything changes. You'll finally know why certain approaches haven't worked - and more importantly, what WILL work for your unique body.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What the DNA Test Reveals */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light mb-8 tracking-wide">
              What Your DNA Test Will Reveal About Your Body
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Stop guessing. Start knowing. Your genetic blueprint holds the answers to why you've struggled with weight, energy, hormones, and performance.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Your Nutrition Blueprint",
                  description: "Why you crave carbs, how you process fats, which foods fuel or drain you, and your optimal macro ratios",
                  icon: "🥗"
                },
                {
                  title: "Hormone Optimization",
                  description: "How your genetics affect estrogen, cortisol, thyroid, and insulin - plus what to do about it",
                  icon: "⚖️"
                },
                {
                  title: "Fitness DNA",
                  description: "Are you built for strength or endurance? How should you train for maximum results?",
                  icon: "🏋️‍♀️"
                },
                {
                  title: "Metabolism Secrets",
                  description: "Your true metabolic type, fat storage patterns, and the fastest path to body recomposition",
                  icon: "🔥"
                }
              ].map((benefit, index) => (
                <div key={index} className="p-6 bg-gradient-to-b from-gray-900/50 to-black border border-gray-700 rounded-lg hover:border-rose-500/50 transition-all duration-300">
                  <div className="text-3xl mb-4">{benefit.icon}</div>
                  <h3 className="text-lg font-medium mb-3 text-rose-300">{benefit.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/g6nrqe5q_IMG_1044.jpeg"
                alt="Professional fitness transformation"
                className="w-full h-auto rounded-lg filter grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-lg"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Inflammation & Gut Health",
                description: "Genetic markers that reveal your body's inflammatory responses, gut sensitivity, and detox capacity",
                icon: "🌿"
              },
              {
                title: "Longevity & Vitality",
                description: "Your genetic aging patterns, vitamin needs, stress resilience, and how to optimize for lifelong health",
                icon: "✨"
              }
            ].map((benefit, index) => (
              <div key={index} className="p-8 bg-gradient-to-b from-gray-900/50 to-black border border-gray-700 rounded-lg hover:border-rose-500/50 transition-all duration-300">
                <div className="text-4xl mb-4 text-center">{benefit.icon}</div>
                <h3 className="text-xl font-medium mb-4 text-rose-300 text-center">{benefit.title}</h3>
                <p className="text-gray-300 leading-relaxed text-center">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-rose-900/30 to-rose-800/30 border border-rose-500/50 rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-medium text-rose-300 mb-4">Finally Understand Why</h3>
              <div className="grid md:grid-cols-2 gap-6 text-gray-300">
                <ul className="space-y-2 text-left">
                  <li>• Diets haven't worked for you</li>
                  <li>• You feel tired despite eating "healthy"</li>
                  <li>• Your workouts don't give you results</li>
                  <li>• Your hormones feel out of control</li>
                </ul>
                <ul className="space-y-2 text-left">
                  <li>• You store fat in certain areas</li>
                  <li>• You have brain fog and mood swings</li>
                  <li>• You struggle with sleep and recovery</li>
                  <li>• Some supplements work, others don't</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Is Essential for Women */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light mb-8 tracking-wide">
              DNA Insight for Every Stage of a Woman's Life
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-gradient-to-b from-rose-900/20 to-transparent border border-rose-500/30 rounded-lg">
              <h3 className="text-2xl font-medium mb-4 text-rose-300">In Your 20s & 30s</h3>
              <p className="text-gray-300 leading-relaxed">
                Build a foundation — fertility, performance, energy, and physique shaping.
              </p>
            </div>
            
            <div className="p-8 bg-gradient-to-b from-rose-900/30 to-transparent border-2 border-rose-500/50 rounded-lg">
              <h3 className="text-2xl font-medium mb-4 text-rose-300">In Your 40s & 50s</h3>
              <p className="text-gray-300 leading-relaxed">
                Navigate perimenopause & menopause with clarity — support hormones, metabolism, and mental sharpness.
              </p>
            </div>
            
            <div className="p-8 bg-gradient-to-b from-rose-900/20 to-transparent border border-rose-500/30 rounded-lg">
              <h3 className="text-2xl font-medium mb-4 text-rose-300">Beyond 50</h3>
              <p className="text-gray-300 leading-relaxed">
                Optimize bone health, longevity, and vitality so you don't just add years to your life, but life to your years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light mb-8 tracking-wide">
              Choose Your Path
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Every tier offers uncompromising excellence, with options for deeper personalization and support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Tier 1 - DNA Blueprint Package */}
            <div className="relative p-8 bg-gradient-to-b from-gray-900 to-black border border-gray-700 luxury-card">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-light mb-4 text-rose-300">DNA Blueprint Package</h3>
                <div className="text-4xl font-light mb-2">$750</div>
                <div className="text-gray-400">one-time</div>
              </div>
              <div className="mb-8">
                <h4 className="text-lg font-medium mb-4 text-white">Unlock Your DNA. Unlock Your Potential.</h4>
                <div className="bg-gradient-to-r from-rose-900/20 to-transparent border border-rose-500/30 rounded-lg p-4 mb-6">
                  <p className="text-rose-300 font-medium italic text-center">
                    Perfect if you're curious and want to uncover how your body really works.
                  </p>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  Your health begins with clarity. This package includes your DNA test kit, advanced genomic analysis, and a personalized blueprint to decode your hormones, metabolism, and health pathways.
                </p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">DNA test kit & analysis</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Full genomic analysis</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Results consultation</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Health blueprint</span>
                </li>
              </ul>
              
              {/* Payment Options */}
              <div className="space-y-3 mb-6">
                <h5 className="text-center text-rose-300 font-medium">Choose Payment Method:</h5>
                
                {/* Stripe Payment */}
                <Button 
                  onClick={() => window.open("https://buy.stripe.com/7sYaEW1M87UG4sE4u2g7e0u", "_blank")}
                  className="w-full bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-none hover:from-rose-600 hover:to-rose-700 transition-all duration-300 py-3"
                >
                  💳 Unlock My DNA Blueprint - $750
                </Button>
                
                {/* Stripe Direct Debit - $215/week */}
                <Button 
                  onClick={() => window.open("https://buy.stripe.com/7sY14m9eAcaW3oA6Cag7e0B", "_blank")}
                  className="w-full bg-transparent border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-white rounded-none transition-all duration-300 py-3"
                >
                  🏦 Payment Plan: $215/week × 4 weeks
                </Button>
                
                {/* Afterpay Payment Option */}
                <button 
                  onClick={() => handleAfterPayPayment('blueprint')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded border-2 border-purple-600 hover:border-purple-700 transition-all duration-300"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>🅰️ Pay with Afterpay</span>
                  </div>
                  <div className="text-sm mt-1">4 payments of $187.50 AUD</div>
                </button>
              </div>
            </div>

            {/* Tier 2 - DNA Revival Elite (Most Popular) */}
            <div className="relative p-8 bg-gradient-to-b from-rose-900/20 to-black border-2 border-rose-500 luxury-card featured">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-rose-500 text-white px-6 py-1 rounded-none uppercase tracking-wider">Most Popular</Badge>
              </div>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-light mb-4 text-rose-300">DNA Revival Elite</h3>
                <div className="text-4xl font-light mb-2">$2,500</div>
                <div className="text-gray-400">complete program</div>
              </div>
              <div className="mb-8">
                <h4 className="text-lg font-medium mb-4 text-white">Your DNA. Your Strategy. Your Transformation.</h4>
                <div className="bg-gradient-to-r from-rose-900/30 to-transparent border-2 border-rose-500/50 rounded-lg p-4 mb-6">
                  <p className="text-rose-200 font-medium italic text-center">
                    For women who want answers AND a personalized action plan to implement them.
                  </p>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  The perfect balance of science and action. Complete DNA testing with personalized blueprint, nutrition and supplement strategies, custom training plan, plus exclusive portal access.
                </p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Everything in DNA Blueprint</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Custom nutrition strategy</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Personalized training plan</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Supplement protocols</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Exclusive portal access</span>
                </li>
              </ul>
              
              {/* Payment Options */}
              <div className="space-y-3 mb-6">
                <h5 className="text-center text-rose-300 font-medium">Choose Payment Method:</h5>
                
                {/* Stripe Payment - Paid in Full */}
                <Button 
                  onClick={() => window.open("https://buy.stripe.com/14AaEW9eA7UG0co9Omg7e0x", "_blank")}
                  className="w-full bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-none hover:from-rose-600 hover:to-rose-700 transition-all duration-300 py-3"
                >
                  💳 Start Elite Transformation - $2,500
                </Button>
                
                {/* Stripe Direct Debit - $215/week */}
                <Button 
                  onClick={() => window.open("https://buy.stripe.com/bJeeVc62o8YK5wIgcKg7e0l", "_blank")}
                  className="w-full bg-transparent border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-white rounded-none transition-all duration-300 py-3"
                >
                  🏦 Payment Plan: $215/week × 12 weeks
                </Button>
                
                {/* Afterpay Payment Option */}
                <button 
                  onClick={() => handleAfterPayPayment('elite')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded border-2 border-purple-600 hover:border-purple-700 transition-all duration-300"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>🅰️ Pay with Afterpay</span>
                  </div>
                  <div className="text-sm mt-1">4 payments of $625 AUD</div>
                </button>
              </div>
            </div>

            {/* Tier 3 - DNA Revival Executive */}
            <div className="relative p-8 bg-gradient-to-b from-gray-900 to-black border border-gray-700 luxury-card">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-light mb-4 text-rose-300">DNA Revival Executive</h3>
                <div className="text-4xl font-light mb-2">$4,500</div>
                <div className="text-gray-400">16-week coaching</div>
              </div>
              <div className="mb-8">
                <h4 className="text-lg font-medium mb-4 text-white">Concierge Coaching for Elite Results.</h4>
                <div className="bg-gradient-to-r from-rose-900/20 to-transparent border border-rose-500/30 rounded-lg p-4 mb-6">
                  <p className="text-rose-300 font-medium italic text-center">
                    The complete concierge experience with weekly coaching and personalised, hands-on support.
                  </p>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  For women who demand transformation at the highest level. Complete DNA package plus 16 weeks of one-on-one coaching with Lisa.
                </p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Everything in Elite</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Weekly coaching sessions</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Direct access to Lisa</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Personal accountability</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">Concierge-level support</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-rose-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300"><span className="font-semibold text-rose-200">4 months complimentary aftercare</span> — built-in support & accountability beyond your program</span>
                </li>
              </ul>
              
              {/* Payment Options */}
              <div className="space-y-3 mb-6">
                <h5 className="text-center text-rose-300 font-medium">Choose Payment Method:</h5>
                
                {/* Stripe Payment - Paid in Full */}
                <Button 
                  onClick={() => window.open("https://buy.stripe.com/00wfZg9eA1wi8IUf8Gg7e0z", "_blank")}
                  className="w-full bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-none hover:from-rose-600 hover:to-rose-700 transition-all duration-300 py-3"
                >
                  💳 Get Executive Coaching - $4,500
                </Button>
                
                {/* Stripe Direct Debit - $290/week */}
                <Button 
                  onClick={() => window.open("https://buy.stripe.com/28E7sK76s8YK9MYf8Gg7e0A", "_blank")}
                  className="w-full bg-transparent border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-white rounded-none transition-all duration-300 py-3"
                >
                  🏦 Payment Plan: $290/week × 16 weeks
                </Button>
                
                {/* Afterpay Payment Option */}
                <button 
                  onClick={() => handleAfterPayPayment('executive')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded border-2 border-purple-600 hover:border-purple-700 transition-all duration-300"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>🅰️ Pay with Afterpay</span>
                  </div>
                  <div className="text-sm mt-1">4 payments of $1,125 AUD</div>
                </button>
              </div>
            </div>
          </div>

          {/* Commitment Banner - Enhanced */}
          <div className="text-center mt-16">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-rose-600 to-rose-700 p-8 rounded-lg border-2 border-rose-400">
                <div className="flex items-center justify-center mb-4">
                  <Crown className="w-8 h-8 text-white mr-3" />
                  <h3 className="text-2xl font-bold text-white tracking-wide">YOUR HEALTH IS OUR COMMITMENT</h3>
                </div>
                <p className="text-white/90 text-sm mb-6 italic">
                  A personalised, science-led approach — built around you.
                </p>
                <p className="text-white text-xl font-medium mb-6 leading-relaxed">
                  "Real, accurate answers and a personalised mapping plan designed for your current health circumstances."
                </p>
                <p className="text-rose-100 text-sm leading-relaxed">
                  Through advanced DNA-based testing and years of clinical experience coaching hundreds of women, you'll receive science-backed insights and a tailored plan built around your unique biology and lifestyle. While individual outcomes naturally vary — and as an Accredited Practising Dietitian I cannot promise specific results — getting you results is my personal commitment. <span className="font-semibold text-white">Executive clients receive an additional 4 months of complimentary aftercare</span> — built-in support, accountability and check-ins so you have more than enough time to apply your plan and feel the difference.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Lisa Section */}
      <section className="py-24 bg-black">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/t6jlluob_IMG_0872.PNG"
                alt="Lisa Saygun - Founder of DNA Revival Women"
                className="w-full h-auto filter grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-rose-900/30 to-transparent"></div>
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-light mb-8 tracking-wide">
                Meet Lisa Saygun
              </h2>
              <p className="text-lg text-rose-300 mb-6 font-medium">
                Founder of DNA Revival Women
              </p>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Lisa Saygun is a 4x World Champion and 6x Australian National Champion, who has competed in 54 shows across 26 years at the elite level. As an Accredited Practising Dietitian, Health Educator, Elite Performance Coach, and Professional Athlete, Lisa is one of Australia's most respected voices in health transformation.
                </p>
                <p className="text-lg">
                  With advanced degrees in Human Movement, Nutrition & Dietetics, Clinical Nutrition, Naturopathy, Sports Psychology, and currently pursuing her Master of Endocrinology, Lisa blends scientific precision with a results-driven approach that has helped hundreds of women transform their bodies and their lives.
                </p>
                <p className="text-lg">
                  Her expertise spans the 6 Pillars of Health — physical, nutritional, hormonal, mental, emotional, and spiritual transformation. With a passion for DNA-based testing and advanced peptide therapy, Lisa bridges the gap between science and lived results.
                </p>
                <p className="text-lg">
                  Her greatest inspiration is her son, Kye, who was born with half a heart and defied impossible odds. His resilience fuels her mission: to show women what is possible when courage, science, and strength collide.
                </p>
              </div>
              <Button 
                onClick={onApply}
                className="mt-8 px-8 py-3 text-base font-light tracking-widest bg-transparent border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-white rounded-none uppercase transition-all duration-500"
              >
                Work With Lisa
              </Button>
            </div>
          </div>
          
          {/* Lisa's Authority Pull Quote */}
          <div className="mt-20">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-rose-900/30 to-rose-800/30 border-2 border-rose-500/50 rounded-lg p-12 text-center">
                <blockquote className="text-2xl md:text-3xl font-light leading-relaxed text-gray-200 mb-8 italic">
                  "After 26 years of competing at the highest level, I've learned one truth: no two women are the same. DNA testing is the missing link that explains why some methods work for one woman but not another. When you know your DNA, you finally know what YOUR body truly needs."
                </blockquote>
                <cite className="text-rose-300 font-medium text-xl">
                  — Lisa Saygun
                </cite>
                <p className="text-gray-400 mt-2">4x World Champion, Accredited Practising Dietitian</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Featured Prominently */}
      <section className="py-32 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-7xl font-light mb-8 tracking-wide">
              Results That Speak Louder Than Recognition
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Real transformations from women who trusted the science of their DNA.
            </p>
          </div>

          {/* Hero Image - Group of Women */}
          <div className="mb-20">
            <div className="relative max-w-5xl mx-auto">
              <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwd29tZW58ZW58MHx8fHwxNzU3OTI2ODI1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Diverse group of confident women"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="text-white text-2xl font-light leading-relaxed">
                    "Women from all walks of life, transforming their health through DNA-driven science."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Health & Wellness Images */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1554244933-d876deb6b2ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxoZWFsdGglMjB3ZWxsbmVzc3xlbnwwfHx8fDE3NTc5MjY4MzF8MA&ixlib=rb-4.1.0&q=85"
                  alt="Women's wellness and health"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rose-900/30 to-transparent"></div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1477332552946-cfb384aeaf1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxoZWFsdGglMjB3ZWxsbmVzc3xlbnwwfHx8fDE3NTc5MjY4MzF8MA&ixlib=rb-4.1.0&q=85"
                  alt="Active healthy lifestyle"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rose-900/30 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Authentic Testimonials Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                quote: "Lisa's approach is life-changing. The DNA insights finally explained why other programs never worked for me. My energy levels are through the roof now.",
                name: "Sarah M.",
                rating: 5
              },
              {
                quote: "After years of feeling frustrated with my health, Lisa's science-based method gave me real answers and results I could see.",
                name: "Michelle K.",
                rating: 5
              },
              {
                quote: "The personalized protocols based on my genetic makeup worked where everything else failed. I finally have control over my health.",
                name: "Rebecca T.",
                rating: 5
              },
              {
                quote: "Lisa doesn't just give you a plan — she gives you the science behind why it works for YOUR unique body.",
                name: "Amanda L.",
                rating: 5
              },
              {
                quote: "For the first time in years, I feel like myself again — only stronger, more energetic, and more confident.",
                name: "Catherine B.",
                rating: 5
              },
              {
                quote: "The DNA testing revealed exactly why I struggled with weight and energy. Lisa's customized approach changed everything.",
                name: "Nicole R.",
                rating: 5
              },
              {
                quote: "Lisa's expertise in genetics and nutrition is unmatched. She transformed not just my body, but my entire relationship with health.",
                name: "Samantha J.",
                rating: 5
              },
              {
                quote: "The science-based approach finally gave me sustainable results. No more guesswork, just clear answers based on my DNA.",
                name: "Emma C.",
                rating: 5
              },
              {
                quote: "Lisa's program addressed my hormonal issues in a way no other coach has. The transformation goes far beyond physical.",
                name: "Jessica P.",
                rating: 5
              },
              {
                quote: "After struggling with menopause symptoms, Lisa's DNA-driven protocols helped me reclaim my energy and vitality.",
                name: "Karen W.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="p-8 bg-gradient-to-b from-gray-900/50 to-black border border-gray-700 luxury-card">
                <div className="mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="inline w-5 h-5 text-rose-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-lg font-light leading-relaxed text-gray-300 mb-6">
                  "{testimonial.quote}"
                </blockquote>
                <cite className="text-rose-300 font-medium">
                  - {testimonial.name}
                </cite>
              </div>
            ))}
          </div>

          {/* Community Image */}
          <div className="text-center mb-16">
            <div className="relative max-w-4xl mx-auto">
              <div className="aspect-[16/9] rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1522543558187-768b6df7c25c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwd29tZW58ZW58MHx8fHwxNzU3OTI2ODI1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Unity and empowerment among women"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8 text-center">
                  <p className="text-white text-xl font-light leading-relaxed">
                    "Join a community of empowered women transforming their lives through science."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Duplicate payment plans section removed - payment options now in pricing cards */}

          {/* Call to Action within testimonials */}
          <div className="text-center">
            <div className="inline-block p-12 bg-gradient-to-r from-rose-900/30 to-rose-800/30 border border-rose-500/30 rounded-lg">
              <p className="text-2xl text-gray-200 mb-8 max-w-3xl leading-relaxed">
                Join hundreds of women who have transformed their health, energy, and confidence through DNA-driven protocols.
              </p>
              <Button 
                onClick={onApply}
                className="px-12 py-4 text-lg font-light tracking-widest bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-none uppercase hover:from-rose-600 hover:to-rose-700 transition-all duration-300"
              >
                Start Your Revival
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Portal Access */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-8 tracking-wide">
            Exclusive Access. Unlimited Empowerment.
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            Join an elite community of women committed to transformation. Access cutting-edge health hacks, expert interviews, and protocols that aren't available anywhere else.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Private Portal",
                description: "Exclusive access to Lisa's vault of health protocols and biohacking strategies"
              },
              {
                icon: <MessageSquare className="h-8 w-8" />,
                title: "Expert Interviews",
                description: "Behind-the-scenes conversations with world-class health and performance experts"
              },
              {
                icon: <Target className="h-8 w-8" />,
                title: "Elite Community",
                description: "Connect with high-achieving women on the same transformation journey"
              }
            ].map((feature, index) => (
              <div key={index} className="p-6 bg-gradient-to-b from-gray-900/30 to-transparent border border-rose-500/20">
                <div className="mb-4 text-rose-400">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-rose-900 to-rose-800">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-6xl font-light mb-8 tracking-wide">
            Your DNA. Your Revival. Begin Today.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto leading-relaxed">
            Every woman deserves to feel energetic, confident, and in control of her health. Your transformation starts here.
          </p>
          <Button 
            onClick={onApply}
            className="px-12 py-4 text-lg font-light tracking-widest bg-white text-rose-900 hover:bg-gray-100 rounded-none uppercase transition-all duration-500"
          >
            Apply Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/ebvs4qol_ChatGPT%20Image%20Aug%2023%2C%202025%20at%2009_01_36%20PM.png"
            alt="Go Figure Coaching Logo"
            className="h-12 w-auto mx-auto mb-6"
          />
          <p className="text-gray-400 text-sm">
            © 2024 DNA Revival Women by Go Figure Coaching. All rights reserved.
          </p>
          <p className="text-rose-300 text-sm mt-2 font-light italic">
            "Your health is my commitment — a personalised, science-led approach, built around you."
          </p>
        </div>
      </footer>

      {/* Modal removed - payment options now directly in pricing cards */}
    </div>
  );
};

// Application Form Component
const ApplicationForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    country: "Australia",
    goals: "",
    experience: "",
    commitment: "",
    package: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Validate package selection
    if (!formData.package) {
      alert("Please select a program package before submitting");
      setIsLoading(false);
      return;
    }
    
    try {
      // Create user profile in backend
      const userResponse = await axios.post(`${API}/users`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: parseInt(formData.age),
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country
      });

      // Create assessment with application data including package selection
      await axios.post(`${API}/assessments`, {
        user_id: userResponse.data.id,
        mutations: [], // Will be filled after DNA test
        current_symptoms: [formData.goals, formData.experience, formData.package], 
        lifestyle_goals: [formData.commitment, `Selected package: ${formData.package}`]
      });

      // Debug and show success message
      console.log("Form Data:", formData);
      console.log("Selected Package:", formData.package);
      
      // Show success alert with package info
      alert(`✅ Application submitted successfully! Package: ${formData.package}`);
      
      // Call onSubmit to handle navigation to thank you page
      onSubmit(formData.package);
    } catch (error) {
      console.error("Application submission error:", error);
      console.log("Error details:", error.response?.data);
      // Still show success and proceed even if there's an error for now
      alert(`✅ Application submitted! Package: ${formData.package}`);
      onSubmit(formData.package);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-16">
          <img 
            src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/ebvs4qol_ChatGPT%20Image%20Aug%2023%2C%202025%20at%2009_01_36%20PM.png"
            alt="Go Figure Coaching Logo"
            className="h-16 w-auto mx-auto mb-8"
          />
          <h1 className="text-4xl md:text-6xl font-light mb-8 tracking-wide">
            Every Woman Deserves This Transformation
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Your DNA holds the key to unlocking your healthiest, most energetic self. Ready to discover what's possible?
          </p>
        </div>

        <div className="bg-gradient-to-b from-gray-900 to-black border border-gray-700 p-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-6">
              <h3 className="text-2xl font-light text-rose-300 border-b border-rose-500/30 pb-4">Personal Information</h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label htmlFor="name" className="text-rose-300 text-lg font-light tracking-wide">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-rose-300 text-lg font-light tracking-wide">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label htmlFor="phone" className="text-rose-300 text-lg font-light tracking-wide">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="+61 400 000 000"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="age" className="text-rose-300 text-lg font-light tracking-wide">Age *</Label>
                  <Input
                    id="age"
                    type="number"
                    min="18"
                    max="100"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="Enter your age"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-6">
              <h3 className="text-2xl font-light text-rose-300 border-b border-rose-500/30 pb-4">DNA Test Kit Delivery Address</h3>
              
              <div>
                <Label htmlFor="address" className="text-rose-300 text-lg font-light tracking-wide">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                  placeholder="123 Main Street, Unit 5"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label htmlFor="city" className="text-rose-300 text-lg font-light tracking-wide">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="Sydney"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state" className="text-rose-300 text-lg font-light tracking-wide">State/Territory *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="NSW"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label htmlFor="postalCode" className="text-rose-300 text-lg font-light tracking-wide">Postal Code *</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                    className="mt-3 bg-transparent border-gray-600 text-white rounded-none h-12"
                    placeholder="2000"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country" className="text-rose-300 text-lg font-light tracking-wide">Country *</Label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="mt-3 bg-transparent border border-gray-600 text-white rounded-none h-12 w-full px-3"
                    required
                  >
                    <option value="Australia" className="bg-gray-800">Australia</option>
                    <option value="New Zealand" className="bg-gray-800">New Zealand</option>
                    <option value="United States" className="bg-gray-800">United States</option>
                    <option value="Canada" className="bg-gray-800">Canada</option>
                    <option value="United Kingdom" className="bg-gray-800">United Kingdom</option>
                    <option value="Other" className="bg-gray-800">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="space-y-6">
              <h3 className="text-2xl font-light text-rose-300 border-b border-rose-500/30 pb-4">Application Details</h3>
              
              <div>
                <Label htmlFor="goals" className="text-rose-300 text-lg font-light tracking-wide">What are your primary health and transformation goals? *</Label>
                <Textarea
                  id="goals"
                  value={formData.goals}
                  onChange={(e) => setFormData({...formData, goals: e.target.value})}
                  className="mt-3 bg-transparent border-gray-600 text-white rounded-none min-h-24"
                  placeholder="e.g., Balance hormones, increase energy, improve sleep, reduce stress..."
                  required
                />
              </div>

              <div>
                <Label htmlFor="experience" className="text-rose-300 text-lg font-light tracking-wide">Describe your previous experience with health programs and what didn't work *</Label>
                <Textarea
                  id="experience"
                  value={formData.experience}
                  onChange={(e) => setFormData({...formData, experience: e.target.value})}
                  className="mt-3 bg-transparent border-gray-600 text-white rounded-none min-h-24"
                  placeholder="Tell us about diets, fitness programs, supplements you've tried..."
                  required
                />
              </div>

              <div>
                <Label htmlFor="commitment" className="text-rose-300 text-lg font-light tracking-wide">Why are you ready to invest in yourself now? *</Label>
                <Textarea
                  id="commitment"
                  value={formData.commitment}
                  onChange={(e) => setFormData({...formData, commitment: e.target.value})}
                  className="mt-3 bg-transparent border-gray-600 text-white rounded-none min-h-24"
                  placeholder="What's driving your decision to prioritize your health transformation now?"
                  required
                />
              </div>

              <div>
                <Label className="text-rose-300 text-lg font-light tracking-wide mb-4 block">Which program interests you most? *</Label>
                <div className="space-y-4">
                  {[
                    { 
                      value: "blueprint", 
                      label: "DNA Blueprint Package", 
                      price: "$750",
                      description: "DNA test + analysis + results consultation + health blueprint"
                    },
                    { 
                      value: "elite", 
                      label: "DNA Revival Elite", 
                      price: "$2,500",
                      description: "Complete package: DNA test + custom nutrition + training + supplement strategy + portal access"
                    },
                    { 
                      value: "executive", 
                      label: "DNA Revival Executive", 
                      price: "$4,500",
                      description: "Everything in Elite + 16 weeks one-on-one coaching with Lisa + weekly check-ins"
                    }
                  ].map((option) => (
                    <div key={option.value} className="p-6 border border-gray-700 hover:border-rose-500/50 transition-colors rounded-lg">
                      <div className="flex items-start space-x-4">
                        <input
                          type="radio"
                          id={option.value}
                          name="package"
                          value={option.value}
                          checked={formData.package === option.value}
                          onChange={(e) => setFormData({...formData, package: e.target.value})}
                          className="w-5 h-5 text-rose-500 mt-1"
                          required
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <Label htmlFor={option.value} className="text-white font-medium text-lg cursor-pointer">
                              {option.label}
                            </Label>
                            <span className="text-rose-300 font-bold text-lg">{option.price}</span>
                          </div>
                          <p className="text-gray-400 text-sm leading-relaxed">{option.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-center pt-8">
              <Button 
                type="submit"
                disabled={isLoading}
                className="px-12 py-4 text-lg font-light tracking-widest bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-none uppercase transition-all duration-300"
              >
                {isLoading ? "Submitting..." : "Submit Application"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Thank You Page
const ThankYouPage = ({ selectedPackage }) => {
  const packageInfo = {
    blueprint: { name: "DNA Blueprint Package", price: "$750" },
    elite: { name: "DNA Revival Elite", price: "$2,500" },
    executive: { name: "DNA Revival Executive", price: "$4,500" }
  };

  const current = packageInfo[selectedPackage] || { name: "Package", price: "N/A" };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_dna-revival-women/artifacts/ebvs4qol_ChatGPT%20Image%20Aug%2023%2C%202025%20at%2009_01_36%20PM.png"
          alt="Go Figure Coaching Logo"
          className="h-20 w-auto mx-auto mb-8"
        />
        <div className="mb-8">
          <Crown className="w-16 h-16 text-rose-400 mx-auto mb-8" />
        </div>
        <h1 className="text-4xl md:text-6xl font-light mb-8 tracking-wide">
          ✅ Application Successfully Submitted!
        </h1>
        <div className="mb-8 p-6 bg-gradient-to-r from-rose-600/20 to-rose-500/20 border border-rose-400/30 rounded-lg">
          <h2 className="text-2xl font-medium text-rose-300 mb-2">Selected Package:</h2>
          <p className="text-3xl font-light text-white">{current.name}</p>
          <p className="text-xl text-rose-300">{current.price}</p>
        </div>
        <p className="text-xl text-gray-300 mb-8 leading-relaxed">
          Your application has been received and Lisa's team will contact you within 24 hours. A payment window will open shortly to complete your investment.
        </p>
        <div className="bg-gradient-to-r from-rose-900/30 to-rose-800/30 border border-rose-500/30 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-medium text-rose-300 mb-4">What Happens Next:</h3>
          <ul className="text-left text-gray-300 space-y-2">
            <li>✅ Application submitted and received</li>
            <li>💳 Complete payment in the window that opened</li>
            <li>📞 Lisa's team will contact you within 24 hours</li>
            <li>📦 DNA test kit shipped within 2-3 business days</li>
            <li>🧬 Complete your DNA test and return it</li>
            <li>📊 Receive your personalized transformation protocol</li>
          </ul>
        </div>
        <p className="text-lg text-rose-300 mb-4">
          Your transformation journey starts now.
        </p>
        <p className="text-sm text-gray-400 italic">
          {selectedPackage === "executive"
            ? '"Your health is my commitment — a personalised plan, weekly 1:1 coaching, plus 4 months of complimentary aftercare."'
            : '"Your health is my commitment — a personalised, science-led plan, built around you."'}
        </p>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [currentView, setCurrentView] = useState("landing");
  const [selectedPackage, setSelectedPackage] = useState("");

  const handleGetStarted = () => {
    setCurrentView("application");
  };

  const handleApply = () => {
    setCurrentView("application");
  };

  const handleApplicationSubmit = (packageType) => {
    console.log("handleApplicationSubmit called with:", packageType);
    
    // Store the selected package
    setSelectedPackage(packageType);
    
    // Show thank you page first
    setCurrentView("thankyou");
    
    // Then open payment window after 3 seconds
    setTimeout(() => {
      const paymentLinks = {
        blueprint: "https://buy.stripe.com/7sYaEW1M87UG4sE4u2g7e0u", // $750 paid in full
        elite: "https://buy.stripe.com/14AaEW9eA7UG0co9Omg7e0x", // $2,500 paid in full
        executive: "https://buy.stripe.com/00wfZg9eA1wi8IUf8Gg7e0z" // $4,500 paid in full
      };
      
      console.log("Opening payment link for package:", packageType);
      if (packageType && paymentLinks[packageType]) {
        window.open(paymentLinks[packageType], '_blank');
      }
    }, 3000);
  };

  const handleAfterPayPayment = (packageType) => {
    console.log("Afterpay payment selected for:", packageType);
    
    // Store the selected package
    setSelectedPackage(packageType);
    
    // Show thank you page first
    setCurrentView("thankyou");
    
    // Then open Afterpay payment after 3 seconds
    setTimeout(() => {
      const afterpayLinks = {
        blueprint: "https://portal.afterpay.com/AU/checkout?amount=750", // $750 in 4 payments
        elite: "https://portal.afterpay.com/AU/checkout?amount=2500", // $2,500 in 4 payments  
        executive: "https://portal.afterpay.com/AU/checkout?amount=4500" // $4,500 in 4 payments
      };
      
      console.log("Opening Afterpay for package:", packageType);
      if (packageType && afterpayLinks[packageType]) {
        window.open(afterpayLinks[packageType], '_blank');
      }
    }, 3000);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={
            <>
              {currentView === "landing" && (
                <DNARevivalLandingPage 
                  onGetStarted={handleGetStarted} 
                  onApply={handleApply} 
                />
              )}
              {currentView === "application" && (
                <ApplicationForm onSubmit={handleApplicationSubmit} />
              )}
              {currentView === "thankyou" && <ThankYouPage selectedPackage={selectedPackage} />}
            </>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;